import React, { useState, useEffect, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Row, Col, Button } from 'react-bootstrap';
import AppContext from 'Utils/AppContext';
import FetchContainer from 'Utils/FetchContainer';
import { filterTableResponseForDisplay } from 'Utils/entityLevels';
import { getTableResult, mapTableResultToFoundEntitiesShape } from 'DataDiscovery/discoverApi';
import TableEntityFeed, { structuredColumnPrefix } from './TableEntityFeed';

/**
 * Dedicated per-column structured view (/results/:search/embedded/:table/:column). A JSON/XML column's
 * leaf paths arrive as compound field keys (e.g. `extra_metadata.ssn`) in the same table result the
 * table detail already fetches — so this page reuses that exact fetch, filters the field map to the one
 * column's subtree, and renders the existing flat/tree TableEntityFeed over it. No popup, no new wire
 * shape. `columnsView` is intentionally omitted so the reused feed renders the structured view
 * (the same shared view a parsed JSON/XML file source uses) rather than the columns view, and
 * `structuredSource` makes tree the default mode since this page is always showing tree data.
 */
const StructuredColumnPage = (props) => {
  const params = useParams();
  const history = useHistory();
  const { entityLevels } = useContext(AppContext);
  const searchid = props.searchid ?? params.search;
  const table = props.table ?? params.table;
  const column = props.column ?? params.column;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [columnFields, setColumnFields] = useState({});
  const [sampledRowCount, setSampledRowCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getTableResult(searchid, table);
        const filtered = filterTableResponseForDisplay(response, entityLevels);
        const foundEntities = mapTableResultToFoundEntitiesShape(filtered);
        const allFields = foundEntities[table] || {};
        // Keep only the leaf paths (and the whole-column finding) that belong to this column.
        const subset = {};
        Object.keys(allFields).forEach((k) => {
          if (structuredColumnPrefix(k) === column) subset[k] = allFields[k];
        });
        if (!cancelled) {
          setColumnFields(subset);
          setSampledRowCount(foundEntities.rows_count || 0);
        }
      } catch (e) {
        console.log(e);
        if (!cancelled) setError(e?.message || 'Failed to load structured column');
      }
      if (!cancelled) setLoading(false);
    };
    if (searchid && table && column) init();
    return () => { cancelled = true; };
  }, [searchid, table, column, entityLevels]);

  const pathCount = Object.keys(columnFields).length;

  return (
    <FetchContainer fetchingData={loading} error={error} absolute={true}>
      <div data-testid='structured-column-page'>
        <Row className='widget-row'>
          <Col className='text-left'>
            <Button
              variant='link'
              size='sm'
              style={{ padding: 0 }}
              onClick={() => history.push(`/results/${searchid}/ALL/${table}`)}
            >
              ← Back to {table}
            </Button>
            <h5 className='mt-2' style={{ fontFamily: 'Rubik' }}>
              Structured paths in <code>{column}</code>
            </h5>
          </Col>
        </Row>
        <Row>
          <Col>
            {pathCount === 0 && !loading ? (
              <div className='text-center' style={{ padding: 48, color: '#8A92A0' }}>
                No structured paths with findings were detected in this column.
              </div>
            ) : (
              <TableEntityFeed
                fields={columnFields}
                table={table}
                searchid={searchid}
                entity='ALL'
                sampledRowCount={sampledRowCount}
                pathDisplayPrefix={column}
                structuredSource={true}
                setError={(e) => setError(e)}
              />
            )}
          </Col>
        </Row>
      </div>
    </FetchContainer>
  );
};

export default StructuredColumnPage;
